import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, Package, Lock, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart, formatPrice } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, total } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  const handleCheckout = async () => {
    if (!items.length || checkingOut) return;
    setCheckingOut(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Checkout failed", {
        description: err?.response?.data?.detail || "Please try again.",
      });
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed top-0 right-0 bottom-0 z-[90] w-full max-w-md bg-panel border-l border-white/10 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
              <h2 className="font-display font-bold uppercase tracking-tight text-lg">
                Your Cart
              </h2>
              <button
                data-testid="cart-close-btn"
                onClick={() => setIsOpen(false)}
                className="text-slate-400 hover:text-white transition-colors"
                aria-label="Close cart"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-4" data-testid="cart-empty-state">
                  <Package size={36} className="text-slate-600" strokeWidth={1.5} />
                  <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-slate-500">
                    No units selected
                  </p>
                </div>
              ) : (
                items.map((item) => (
                  <motion.div
                    key={item.slug}
                    data-testid={`cart-item-${item.slug}`}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex gap-4 border border-white/8 bg-black/30 p-4"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-24 object-cover grayscale-[25%]"
                    />
                    <div className="flex-1 flex flex-col justify-between">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-display font-bold uppercase text-sm tracking-tight">
                            {item.name}
                          </h3>
                          <p className="font-mono-tech text-xs text-cyber mt-1">
                            {item.price}
                          </p>
                        </div>
                        <button
                          data-testid={`cart-remove-${item.slug}`}
                          onClick={() => removeItem(item.slug)}
                          className="text-slate-500 hover:text-magma transition-colors"
                          aria-label={`Remove ${item.name}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <button
                          data-testid={`cart-qty-minus-${item.slug}`}
                          onClick={() => updateQty(item.slug, item.qty - 1)}
                          className="w-7 h-7 border border-white/15 flex items-center justify-center hover:border-magma hover:text-magma transition-colors"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="font-mono-tech text-sm w-4 text-center" data-testid={`cart-qty-${item.slug}`}>
                          {item.qty}
                        </span>
                        <button
                          data-testid={`cart-qty-plus-${item.slug}`}
                          onClick={() => updateQty(item.slug, item.qty + 1)}
                          className="w-7 h-7 border border-white/15 flex items-center justify-center hover:border-magma hover:text-magma transition-colors"
                          aria-label="Increase quantity"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/8 px-6 py-6 space-y-5">
                <div className="flex items-center gap-3 text-slate-400">
                  <Lock size={14} className="text-cyber shrink-0" />
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] leading-relaxed">
                    Stealth packaging. Neutral billing descriptor. No logos, ever.
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-slate-400">
                    Subtotal
                  </span>
                  <span className="font-display font-black text-2xl" data-testid="cart-total">
                    {formatPrice(total)}
                  </span>
                </div>
                <button
                  data-testid="stripe-checkout-btn"
                  onClick={handleCheckout}
                  disabled={checkingOut}
                  className="w-full bg-magma text-white font-display font-bold uppercase tracking-widest text-sm py-4 glow-magenta hover:bg-[#ff4d87] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {checkingOut ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Securing session…
                    </>
                  ) : (
                    "Secure Checkout"
                  )}
                </button>
                <p className="text-center font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-600">
                  Encrypted payment via Stripe // 18+ verified
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
